import { getProductSpecificationFlags } from "@/utils/productSpecificationTypeUtils";

export type PackagingVisibility = {
  showTotalNumberOfComponents: boolean;
  showCrvRequired: boolean;
  showTamperEvident: boolean;
};

export const PACKAGING_VISIBILITY_BY_SPEC: Record<string, PackagingVisibility> = {
  'Food & Beverages': {
    showTotalNumberOfComponents: false,
    showCrvRequired: true,
    showTamperEvident: true,
  },
  'All Branded Products': {
    showTotalNumberOfComponents: false,
    showCrvRequired: true,
    showTamperEvident: true,
  },
  'HABA (Health and Beauty Aids)': {
    showTotalNumberOfComponents: false,
    showCrvRequired: true,
    showTamperEvident: true,
  },
  'Pet Foods': {
    showTotalNumberOfComponents: false,
    showCrvRequired: true,
    showTamperEvident: true,
  },
};

export const getPackagingVisibility = (rawSpecType: string | undefined | null): PackagingVisibility => {
  const normalized = (rawSpecType || '').replace(/&amp;/g, '&').trim();
  return PACKAGING_VISIBILITY_BY_SPEC[normalized] || {
    showTotalNumberOfComponents: false,
    showCrvRequired: true,
    showTamperEvident: true,
  };
};

// Section-level visibility for large layout differences per spec
export type PackagingSectionsVisibility = {
  packagingComponents: boolean;
  recyclingIcons: boolean;
  otherLabellingInfo: boolean;
  productLabellingDetails: boolean;
  weightUnitsSpecification: boolean;
  productSpecificIconsLegends: boolean;
  kosherSymbol: boolean;
  kosherDesignation: boolean;
  claimsDisclaimerStatements: boolean;
  petFoodClaimsDisclaimerStatements: boolean;
  iconsAndClaims: boolean;
  regionSpecificIcons: boolean;
  nutritionDietaryIcons: boolean;
  claimsDisclaimerNotPetFoods: boolean;
  safetyProcessingEnvironmental: boolean;
  storageHandlingDistribution: boolean;
  codingStatements: boolean;
  additionalCertificates: boolean;
  printerInformation: boolean;
  printerDielineDetails: boolean;
  artworkApproverDetails: boolean;
  organicClaim: boolean;
  organicCertifier: boolean;
  bottleRedemptionInformation: boolean;
  nonCopyInformation: boolean;
  documents: boolean;
  cookingInstructions: boolean;
  countryOfOriginStatements: boolean;
  quantitySeparable: boolean;
};

const defaultSections: PackagingSectionsVisibility = {
  recyclingIcons: true,
  otherLabellingInfo: true,
  weightUnitsSpecification: true,
  iconsAndClaims: true,
  regionSpecificIcons: true,
  nutritionDietaryIcons: true,
  claimsDisclaimerNotPetFoods: true,
  safetyProcessingEnvironmental: true,
  storageHandlingDistribution: true,
  codingStatements: true,
  additionalCertificates: true,
  printerInformation: true,
  printerDielineDetails: true,
  artworkApproverDetails: true,
  organicClaim: true,
  organicCertifier: true,
  bottleRedemptionInformation: true,
  nonCopyInformation: true,
  documents: true,
  cookingInstructions: true,
  claimsDisclaimerStatements: true,
  countryOfOriginStatements: true,
  petFoodClaimsDisclaimerStatements: true,
  productLabellingDetails: true,
  productSpecificIconsLegends: true,
  kosherSymbol: true,
  kosherDesignation: true,
  packagingComponents: true,
  quantitySeparable: true,
};

/** Other Labeling sections for Produce RAC - Trader Joe's Label datapoints. */
export const PRODUCE_RAC_TJ_LABEL_OTHER_LABELING_SECTIONS: PackagingSectionsVisibility = {
  recyclingIcons: true,
  productLabellingDetails: true,
  weightUnitsSpecification: true,
  claimsDisclaimerStatements: true,
  kosherSymbol: true,
  kosherDesignation: true,
  codingStatements: true,
  storageHandlingDistribution: true,
  safetyProcessingEnvironmental: true,
  countryOfOriginStatements: true,
  printerDielineDetails: true,
  artworkApproverDetails: true,
  organicClaim: true,
  organicCertifier: true,
  nonCopyInformation: true,
  documents: true,
  packagingComponents: false,
  otherLabellingInfo: false,
  productSpecificIconsLegends: false,
  iconsAndClaims: false,
  regionSpecificIcons: false,
  nutritionDietaryIcons: false,
  claimsDisclaimerNotPetFoods: false,
  additionalCertificates: false,
  printerInformation: false,
  bottleRedemptionInformation: false,
  petFoodClaimsDisclaimerStatements: false,
  cookingInstructions: false,
  quantitySeparable: false,
};

export const getPackagingSectionsVisibility = (
  rawSpecType: string | undefined | null
): PackagingSectionsVisibility => {
  const flags = getProductSpecificationFlags(rawSpecType ?? "");
  if (flags.isProduceRacTradersJoeLabel) {
    return { ...PRODUCE_RAC_TJ_LABEL_OTHER_LABELING_SECTIONS };
  }

  const normalized = flags.normalizedSpecType || (rawSpecType || '').replace(/&amp;/g, '&').trim();
  if (normalized === 'Food & Beverages' || normalized === 'All Branded Products') {
    return { ...defaultSections };
  }
  if (normalized === 'HABA' || normalized === 'HABA (Health and Beauty Aids)') {
    return {
      ...defaultSections,
      nutritionDietaryIcons: false,
      claimsDisclaimerStatements: true,
    };
  }
  if (normalized === 'Pet Foods') {
    return {
      ...defaultSections,
      nutritionDietaryIcons: false,
      claimsDisclaimerNotPetFoods: false,
    };
  }
  return { ...defaultSections };
};
